import { useQuery } from '@apollo/client';
import { RuleVariant, RuleVariantUnion } from '@procureai/rule-builder';
import { OnSubmit, RuleBuilderProps, UseRuleBuilderBackend } from '@procureai/rule-builder-ui-mui';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { indexBy, prop } from 'ramda';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useRuleVersionActionsContext } from '../../components/RuleVersionActionsProvider/RuleVersionActionsProvider.context.js';
import { GetRuleRuleBuilderBackendDataQuery, TsrctRuleVersionStatus } from '@/generated/gql/graphql.js';
import { I18nNamespace } from '~/common/types/I18nNamespace.js';

import { GET_RULE_BUIDER_BACKEND_DATA } from './Rule.query.gql.js';

interface UseRuleBuilderBackendProps {
  id: string;
}

export interface RuleCreateInputData {
  variant: RuleVariantUnion;
  name: string;
  responsibleUsers: string[];
}

// callback 'clear' is leveraged internally by localStorage backend only and not implemented in the frontend
type UseRuleBuilderBackendResult = Omit<ReturnType<UseRuleBuilderBackend>, 'clear' | 'save'> & {
  stats: RuleBuilderProps['stats'] | undefined;
  variant: RuleVariantUnion;
  onSubmit: OnSubmit;
  onValidationError: () => void;
};

// ### Helper functions ###
const getGraph = (data: GetRuleRuleBuilderBackendDataQuery | undefined) =>
  (data?.tesseract.rule.ruleVersion?.graph as ReturnType<UseRuleBuilderBackend>['data']) ?? null;

export const useRuleBuilderBackend = ({ id }: UseRuleBuilderBackendProps): UseRuleBuilderBackendResult => {
  const { t } = useTranslation(I18nNamespace.ruleBuilder, { keyPrefix: 'DetailPage' });
  const { onError } = useCustomSnackbar();
  const { data, loading } = useQuery(GET_RULE_BUIDER_BACKEND_DATA, {
    variables: {
      id,
    },
    onError,
  });

  const { onSubmit, loading: saving } = useRuleVersionActionsContext();

  const onValidationError = useCallback(() => onError(t('validationError')), [t, onError]);
  const graph = useMemo<ReturnType<UseRuleBuilderBackend>['data']>(() => getGraph(data), [data]);

  const ruleVersion = data?.tesseract.rule.ruleVersion;
  const statsData = ruleVersion?.stats;
  const ruleVersionStatus = ruleVersion?.status;
  const stats = useMemo<RuleBuilderProps['stats']>(() => {
    return ruleVersionStatus === TsrctRuleVersionStatus.TAGGED && statsData
      ? {
          nodes: indexBy(prop('itemId'), statsData.nodes),
          edges: indexBy(prop('itemId'), statsData.edges),
        }
      : undefined;
  }, [statsData, ruleVersionStatus]);

  return {
    data: graph,
    stats,
    variant: data?.tesseract.rule?.variant || RuleVariant.SOURCING,
    loading,
    saving,
    onSubmit,
    onValidationError,
  };
};
